import { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, User as FirebaseUser } from 'firebase/auth';
import { auth } from '../../firebase';
import { saveUserIdentityViaAPI } from '../../services/auth';
import { registerUser, UserRole } from '../../services/user.service';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail } from 'lucide-react';
import logo from '../../assets/logo.gif';
import { Link } from 'react-router-dom';

export default function SignupFlow() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { setUserData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();


  // Pre-fill email from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const emailFromUrl = searchParams.get('email');
    
    if (emailFromUrl) {
      setEmail(emailFromUrl);
    }
  }, [location.search]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!email?.trim()) {
      newErrors.email = 'Email is required';
    }
    
    if (!password) {
      newErrors.password = 'Password is required';
    }
    
    if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords don't match";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const completeSignIn = async (firebaseUser: FirebaseUser) => {

    const authResponse = await saveUserIdentityViaAPI(firebaseUser);
    if (!authResponse || !authResponse.jwt) {
      throw new Error('JWT not found in auth API response');
    }
    

    const registerData = await registerUser(firebaseUser.email || '');
    const actualData: any = registerData.data?.data || registerData.data;
    const userRole: UserRole | null = actualData?.role || null;
    
    // Set user data with empty profile (needs to be completed)
    setUserData(firebaseUser, userRole, null);
    
    // Always redirect to update-profile for new signups
    navigate('/update-profile');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setIsSubmitting(true);
    
    try {
      // Create Firebase account
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      // Complete backend registration and redirect to profile form
      await completeSignIn(cred.user);
    } catch (error: any) {
      console.error('Error creating account:', error);
      let errorMessage = 'Signup failed. Please try again.';
      
      if (error?.code === 'auth/email-already-in-use') {
        errorMessage = 'An account with this email already exists.';
      } else if (error?.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters.';
      } else if (error?.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      }
      
      setErrors(prev => ({ ...prev, submit: errorMessage }));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-[#161513] flex flex-col relative">
      {/* Logo Background */}
      <div 
        className="absolute top-0 left-0 right-0 h-[400px] bg-cover bg-center bg-no-repeat opacity-20 z-0 pointer-events-none"
        style={{ backgroundImage: `url(${logo})` }}
      ></div>
    
      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-[#0E0D0C]/95 backdrop-blur-lg border-b border-[#0CB4E4]/20 py-3 px-3 sm:px-6 sm:py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1.5 sm:gap-3">
            <img src={logo} alt="CR3ATE.AI" className="h-6 sm:h-10" />
            <h1 className="font-light text-base sm:text-2xl text-[#0CB4E4] font-brand">CR3ATE.AI</h1>
          </Link>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="max-w-md w-full">
          <div className="bg-[#161513] rounded-2xl shadow-xl border border-[#0CB4E4]/30 overflow-hidden">
            <div className="p-6 md:p-8 border-b border-[#0CB4E4]/20">
              <h2 className="text-2xl font-bold text-white mb-2">Create your account</h2>
              <p className="text-gray-300">Enter your email and password to get started.</p>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                  Email <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 rounded-xl bg-[#161513] border ${
                      errors.email ? 'border-red-500 bg-red-900/10' : 'border-[#0CB4E4]/30'
                    } focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] transition-colors text-white`}
                    placeholder="you@example.com"
                  />
                </div>
                {errors.email && <p className="mt-2 text-sm text-red-400">{errors.email}</p>}
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-white mb-2">
                  Password <span className="text-red-400">*</span>
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl bg-[#161513] border ${
                    errors.password ? 'border-red-500 bg-red-900/10' : 'border-[#0CB4E4]/30'
                  } focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] transition-colors text-white`}
                  placeholder="••••••••"
                />
                {errors.password && <p className="mt-2 text-sm text-red-400">{errors.password}</p>}
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-white mb-2">
                  Confirm Password <span className="text-red-400">*</span>
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl bg-[#161513] border ${
                    errors.confirmPassword ? 'border-red-500 bg-red-900/10' : 'border-[#0CB4E4]/30'
                  } focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] transition-colors text-white`}
                  placeholder="••••••••"
                />
                {errors.confirmPassword && <p className="mt-2 text-sm text-red-400">{errors.confirmPassword}</p>}
              </div>
              
              {/* Submit error */}
              {errors.submit && (
                <div className="bg-red-900/20 border border-red-400/30 text-red-400 px-4 py-3 rounded-xl text-sm">
                  {errors.submit}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 px-6 bg-gradient-to-r from-[#0CB4E4] to-[#0CB4E4]/80 text-[#161513] font-medium rounded-xl shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#161513]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating account...
                  </div>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>
          </div>
          
          {/* Login link */}
          <div className="text-center mt-6">
            <p className="text-sm text-gray-300">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="text-[#0CB4E4] underline underline-offset-2 hover:opacity-90"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}